// =======================
// TrialBalancePDF.js
// =======================
import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";

// =======================
// STYLES
// =======================
const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: "Helvetica",
    flexDirection: "column",
    padding: 0,
  },
  table: {
    display: "table",
    width: "auto",
    marginTop: 5,
  },
  tableRow: { flexDirection: "row" },
  firstCol: { borderLeftWidth: 1 },
  topBorder: { borderTopWidth: 1 },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    borderTopWidth: 0,
    borderLeftWidth: 0,
    padding: 4,
    flexWrap: "wrap",
    wordBreak: "break-word",
  },
  tableColHeader: {
    backgroundColor: "#e0e0e0",
    fontWeight: "bold",
    textAlign: "center",
  },
  nodeRow: {
    backgroundColor: "#cce5ff",
    fontWeight: "bold",
    padding: 4,
  },
  groupRow: {
    backgroundColor: "#e6f2ff",
    fontWeight: "bold",
    padding: 4,
  },
  subtotalRow: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    padding: 4,
  },
  totalRow: {
    backgroundColor: "#b3b3b3",
    fontWeight: "bold",
    padding: 4,
  },
  textLeft: { textAlign: "left" },
  textRight: { textAlign: "right" },
  headerImage: { padding: 2 },
});

// =======================
// HEADER
// =======================
let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
  ? JSON.parse(localStorage.getItem("apiPaths"))
  : "-";

const Header = ({ parentDetails }) => (
  <View style={{ width: "100%" }} fixed>
    <View style={{ padding: 10 }}>
      <View style={{ width: "100%", flexDirection: "column" }}>
        {apiPaths_from_local_storage[0]?.REP_HEADER?.includes("data:image/") && (
          <Image
            src={apiPaths_from_local_storage[0].REP_HEADER}
            style={styles.headerImage}
          />
        )}
        <View style={{ textAlign: "center", marginTop: 10 }}>
          <Text style={{ fontSize: 12, fontWeight: "bold", marginTop: 5 }}>
            Trial Balance Report
          </Text>
        </View>
      </View>
    </View>
  </View>
);

// =======================
// FOOTER
// =======================
const Footer = () => (
  <View style={{ width: "100%" }} fixed>
    <View style={{ width: "100%", padding: 10 }}>
      <View style={{ width: "100%", fontSize: 10 }}>
        <Text
          style={{ textAlign: "center" }}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
        />
      </View>
    </View>
  </View>
);

// =======================
// TABLE CONTENT
// =======================
const TableContent = ({ groupedData, columns, colWidths, grandTotal }) => (
  <View style={{ flexGrow: 1 }} wrap>
    <View style={{ padding: 10 }}>
      {/* Table Header */}
      <View style={styles.tableRow} fixed>
        {columns.map((col, i) => {
          const isAccountCol = col.column_key_from_api === "ACCOUNT";
          return (
            <View
              key={i}
              style={[
                styles.tableCol,
                styles.tableColHeader,
                styles.topBorder,
                i === 0 && styles.firstCol,
                isAccountCol ? { width: 70, borderLeftWidth: 1 } : { flex: colWidths[i] },
              ]}
            >
              <Text>{col.column_name}</Text>
            </View>
          );
        })}
      </View>

      {/* Table Body */}
      {Object.entries(groupedData).map(([node, groups], nodeIndex) => (
        <React.Fragment key={nodeIndex}>
          {/* Node Header */}
          <View style={styles.tableRow} wrap={false}>
            <View
              style={[
                styles.tableCol,
                styles.nodeRow,
                styles.firstCol,
                { flex: colWidths.reduce((a, b) => a + b, 0) },
              ]}
            >
              <Text>{node}</Text>
            </View>
          </View>

          {/* Each Group Code */}
          {Object.entries(groups).map(([groupCode, rows], groupIndex) => {
            // Group subtotal calculated based on calculated Row Debits and Credits
            const groupSubtotal = rows.reduce(
              (acc, r) => {
                const bal = r.BALANCE || 0;
                const rowDebit = bal > 0 ? bal : 0;
                const rowCredit = bal < 0 ? Math.abs(bal) : 0;
                return {
                  DEBIT: acc.DEBIT + rowDebit,
                  CREDIT: acc.CREDIT + rowCredit,
                };
              },
              { DEBIT: 0, CREDIT: 0 }
            );

            return (
              <React.Fragment key={groupIndex}>
                {/* Group Header */}
                <View style={styles.tableRow} wrap={false}>
                  <View
                    style={[
                      styles.tableCol,
                      styles.groupRow,
                      styles.firstCol,
                      { flex: colWidths.reduce((a, b) => a + b, 0) },
                    ]}
                  >
                    <Text>{groupCode}</Text>
                  </View>
                </View>

                {/* Group Rows */}
                {rows.map((row, i) => {
                  const bal = row.BALANCE || 0;
                  const displayDebit = bal > 0 ? bal.toFixed(2) : "0.00";
                  const displayCredit = bal < 0 ? Math.abs(bal).toFixed(2) : "0.00";

                  return (
                    <View style={styles.tableRow} key={i} wrap={false}>
                      {columns.map((col, ci) => {
                        const isAccountCol = col.column_key_from_api === "ACCOUNT";
                        
                        let cellValue = row[col.column_key_from_api] ?? "";
                        if (col.column_key_from_api === "DEBIT") cellValue = displayDebit;
                        if (col.column_key_from_api === "CREDIT") cellValue = displayCredit;

                        return (
                          <View
                            key={ci}
                            style={[
                              styles.tableCol,
                              ci === 0 && styles.firstCol,
                              isAccountCol ? { width: 70, borderLeftWidth: 1 } : { flex: colWidths[ci] },
                            ]}
                          >
                            <Text
                              style={
                                ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                                  ? styles.textRight
                                  : styles.textLeft
                              }
                            >
                              {cellValue}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  );
                })}

                {/* Group Subtotal */}
                <View style={styles.tableRow} wrap={false}>
                  {columns.map((col, ci) => {
                    const isAccountCol = col.column_key_from_api === "ACCOUNT";
                    let value = "";
                    if (ci === 0) value = "Subtotal";
                    else if (["DEBIT", "CREDIT"].includes(col.column_key_from_api))
                      value = groupSubtotal[col.column_key_from_api].toFixed(2);

                    return (
                      <View
                        key={ci}
                        style={[
                          styles.tableCol,
                          styles.subtotalRow,
                          ci === 0 && styles.firstCol,
                          isAccountCol ? { width: 70, borderLeftWidth: 1 } : { flex: colWidths[ci] },
                        ]}
                      >
                        <Text
                          style={
                            ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                              ? styles.textRight
                              : styles.textLeft
                          }
                        >
                          {value}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </React.Fragment>
            );
          })}

          {/* Node Total */}
          <View style={styles.tableRow} wrap={false}>
            {(() => {
              // Calculate Node Total based on group totals
              const nodeTotal = Object.values(groups).reduce((nodeAcc, rows) => {
                const groupTotal = rows.reduce(
                  (acc, r) => {
                    const bal = r.BALANCE || 0;
                    const rowDebit = bal > 0 ? bal : 0;
                    const rowCredit = bal < 0 ? Math.abs(bal) : 0;
                    return {
                      DEBIT: acc.DEBIT + rowDebit,
                      CREDIT: acc.CREDIT + rowCredit,
                    };
                  },
                  { DEBIT: 0, CREDIT: 0 }
                );
                return {
                  DEBIT: nodeAcc.DEBIT + groupTotal.DEBIT,
                  CREDIT: nodeAcc.CREDIT + groupTotal.CREDIT,
                };
              }, { DEBIT: 0, CREDIT: 0 });

              return columns.map((col, ci) => {
                const isAccountCol = col.column_key_from_api === "ACCOUNT";
                let value = "";
                if (ci === 0) value = "Node Total";
                else if (["DEBIT", "CREDIT"].includes(col.column_key_from_api))
                  value = nodeTotal[col.column_key_from_api].toFixed(2);

                return (
                  <View
                    key={ci}
                    style={[
                      styles.tableCol,
                      styles.totalRow,
                      ci === 0 && styles.firstCol,
                      isAccountCol ? { width: 70, borderLeftWidth: 1 } : { flex: colWidths[ci] },
                    ]}
                  >
                    <Text
                      style={
                        ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                          ? styles.textRight
                          : styles.textLeft
                      }
                    >
                      {value}
                    </Text>
                  </View>
                );
              });
            })()}
          </View>
        </React.Fragment>
      ))}

      {/* Grand Total */}
      <View style={styles.tableRow} wrap={false}>
        {columns.map((col, ci) => {
          const isAccountCol = col.column_key_from_api === "ACCOUNT";
          let value = "";
          if (ci === 0) value = "Grand Total";
          else if (["DEBIT", "CREDIT"].includes(col.column_key_from_api))
            value = grandTotal[col.column_key_from_api]?.toFixed(2) || "0.00";

          return (
            <View
              key={ci}
              style={[
                styles.tableCol,
                styles.totalRow,
                ci === 0 && styles.firstCol,
                isAccountCol ? { width: 70, borderLeftWidth: 1 } : { flex: colWidths[ci] },
              ]}
            >
              <Text
                style={
                  ["DEBIT", "CREDIT"].includes(col.column_key_from_api)
                    ? styles.textRight
                    : styles.textLeft
                }
              >
                {value}
              </Text>
            </View>
          );
        })}
      </View>
    </View>
  </View>
);

// =======================
// MAIN COMPONENT
// =======================
const TrialBalanceOpeningPDF = ({
  data,
  parentDetails,
  selected_mode_table_structure,
}) => {
  const reportData = data || [];

  const selectedMode = parentDetails.mode_list_array.find((m) => m.is_selected);
  
  // Filter out NODE, GROUP CODE, and explicitly BALANCE column
  const columns = selected_mode_table_structure
    .find((item) => item.mode_table_name === selectedMode?.mode_name)
    ?.mode_table_columns.filter(
      (col) =>
        col.column_key_from_api !== "NODE" &&
        col.column_key_from_api !== "GROUP CODE" &&
        col.column_key_from_api !== "BALANCE"
    ) || [];

  const colWidths = columns.map((col) =>
    ["DEBIT", "CREDIT"].includes(col.column_key_from_api) ? 1 : 2
  );

  const groupedData = reportData.reduce((acc, row) => {
    if (!row.NODE) return acc;
    if (!acc[row.NODE]) acc[row.NODE] = {};
    if (!acc[row.NODE][row["GROUP CODE"]])
      acc[row.NODE][row["GROUP CODE"]] = [];
    
    // Check if there is any numerical data present
    const hasData = ["DEBIT", "CREDIT", "BALANCE"].some((k) => row[k]);
    if (hasData) acc[row.NODE][row["GROUP CODE"]].push(row);
    return acc;
  }, {});

  // Grand total calculation adhering to the custom logic
  const grandTotal = reportData.reduce(
    (acc, r) => {
      const bal = r.BALANCE || 0;
      const rowDebit = bal > 0 ? bal : 0;
      const rowCredit = bal < 0 ? Math.abs(bal) : 0;
      return {
        DEBIT: acc.DEBIT + rowDebit,
        CREDIT: acc.CREDIT + rowCredit,
      };
    },
    { DEBIT: 0, CREDIT: 0 }
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header parentDetails={parentDetails} />
        <TableContent
          groupedData={groupedData}
          columns={columns}
          colWidths={colWidths}
          grandTotal={grandTotal}
        />
        <Footer />
      </Page>
    </Document>
  );
};

export default TrialBalanceOpeningPDF;